<template>
  <router-link
    v-if="bannerData?.title && bannerData.ctaLink"
    :to="bannerData.ctaLink"
    class="fresh-banner"
    @click="onCtaClick"
  >
    <div class="fresh-banner__image">
      <img
        v-if="bannerData.imageUrl"
        :src="bannerData.imageUrl"
        :alt="bannerData.title"
      >
    </div>
    <div class="fresh-banner__content">
      <div class="fresh-banner__header">
        <div
          v-if="bannerData.badgeText"
          class="fresh-banner__badge"
        >
          <AkBadge
            :color="resolvedBadgeColor"
            :content="bannerData.badgeText"
            :size="AkBadgeSize.Xs"
            :symbol="bannerData.badgeIcon || undefined"
          />
        </div>
        <p
          class="fresh-banner__title"
          :style="bannerData.titleColor ? { color: bannerData.titleColor } : undefined"
        >
          {{ bannerData.title }}
        </p>
      </div>
      <AkParagraph
        v-if="descriptionParts"
        class="fresh-banner__description"
      >
        <template
          v-for="(part, index) in descriptionParts"
          :key="index"
        >
          <strong v-if="part.isHighlight">{{ part.text }}</strong>
          <template v-else>
            {{ part.text }}
          </template>
        </template>
      </AkParagraph>
      <AkParagraph
        v-if="description2Parts"
        class="fresh-banner__description"
      >
        <template
          v-for="(part, index) in description2Parts"
          :key="index"
        >
          <strong v-if="part.isHighlight">{{ part.text }}</strong>
          <template v-else>
            {{ part.text }}
          </template>
        </template>
      </AkParagraph>
      <span
        v-if="bannerData.ctaText"
        class="fresh-banner__cta-link"
      >
        {{ bannerData.ctaText }}
      </span>
    </div>
  </router-link>
</template>

<script setup lang="ts">
import { computed, PropType } from 'vue';
import { AkBadge, AkBadgeColor, AkBadgeSize } from '@ankorstore/design-system';
import type { NavItemBannerData } from '@bc/discovery/domain/navigation-menu/prepare-data-for-navigation-menu';
import { TrackingService } from '@bc/discovery/domain/tracking';

const props = defineProps({
  bannerData: {
    type: Object as PropType<NavItemBannerData | null>,
    default: null,
  },
});

const emit = defineEmits<{
  (e: 'cta-click'): void;
}>();

const onCtaClick = () => {
  if (props.bannerData?.title && props.bannerData?.ctaLink) {
    TrackingService.trackBannerCtaClicked(props.bannerData.title, props.bannerData.ctaLink);
  }
  emit('cta-click');
};

const resolvedBadgeColor = computed(() => {
  if (!props.bannerData?.badgeColor) {
    return AkBadgeColor.FrozenAlt;
  }
  return AkBadgeColor[props.bannerData.badgeColor as keyof typeof AkBadgeColor] ?? AkBadgeColor.FrozenAlt;
});

function parseTextWithPlaceholder(
  text: string,
  placeholder: string,
  replacement: string
): Array<{ text: string; isHighlight: boolean }> {
  const parts: Array<{ text: string; isHighlight: boolean }> = [];
  const index = text.indexOf(placeholder);
  if (index === -1) {
    parts.push({ text, isHighlight: false });
  } else {
    const before = text.substring(0, index);
    const after = text.substring(index + placeholder.length);
    if (before) {
      parts.push({ text: before, isHighlight: false });
    }
    if (replacement) {
      parts.push({ text: replacement, isHighlight: true });
    }
    if (after) {
      parts.push({ text: after, isHighlight: false });
    }
  }
  return parts;
}

const descriptionParts = computed(() => {
  if (!props.bannerData?.description) {
    return null;
  }
  return parseTextWithPlaceholder(props.bannerData.description, '{highlight}', props.bannerData.descriptionHighlight || '');
});

const description2Parts = computed(() => {
  if (!props.bannerData?.description2) {
    return null;
  }
  return parseTextWithPlaceholder(props.bannerData.description2, '{highlight}', props.bannerData.descriptionHighlight2 || '');
});
</script>

<style scoped>
.fresh-banner {
  @apply ds-flex ds-flex-col xl:ds-flex-row ds-mt-4 xl:ds-mt-0 ds-mb-4 ds-bg-white ds-rounded-lg ds-shadow-sm ds-overflow-hidden ds-border ds-border-solid ds-border-neutral-300 xl:ds-border-0 ds-no-underline ds-cursor-pointer ds-w-full xl:ds-w-auto xl:ds-gap-3;
}

.fresh-banner__image {
  @apply ds-flex-shrink-0 ds-w-full xl:ds-w-[180px] xl:ds-min-w-[180px] ds-pt-4 xl:ds-pt-0 ds-h-[167px] xl:ds-h-auto;
}

.fresh-banner__image img {
  @apply ds-w-full ds-h-full ds-object-contain xl:ds-object-cover;
}

.fresh-banner__content {
  @apply ds-flex ds-flex-col ds-gap-4 xl:ds-gap-2 ds-justify-between ds-p-4 xl:ds-py-4 xl:ds-pr-4 xl:ds-pl-0;
}

.fresh-banner__header {
  @apply ds-flex ds-items-center ds-gap-2 ds-flex-wrap;
}

.fresh-banner__title {
  @apply ds-text-sm xl:ds-text-base ds-font-semibold xl:ds-font-bold ds-m-0 ds-text-primary;
  line-height: 1.43;
}

.fresh-banner__description {
  @apply ds-text-sm ds-m-0 ds-text-neutral-900;
  line-height: 1.4;
}

.fresh-banner__cta-link {
  @apply ds-text-sm ds-font-normal xl:ds-font-semibold ds-underline ds-m-0 ds-text-primary;
  line-height: 1.4;
}
</style>
